import { APP_ROUTES } from '@/constants/pages';
import { locationSlug, slug } from '@/helpers/urlTs';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
} from '@/hooks/search/useSearch.constants';
import { getKeywordWithSlug, getLocationWithSlug } from '@/hooks/search/useSearch.helpers';
import { SerpTabQuery } from '@/reducers/unifiedSearchReducer';
import { matchPath } from 'react-router-dom';
import { SerpTabId } from './SerpTabs.types';

export type SerpSearch = { q: string; location: string; lat?: string | number; lon?: string | number };

const TAB_PATHS: Record<SerpTabId, { indexable: string; noIndex: string; prefix: string }> = {
  places: { indexable: APP_ROUTES.SERP, noIndex: APP_ROUTES.SERP_NO_INDEX, prefix: '' },
  sistaminuten: {
    indexable: APP_ROUTES.SISTA_MINUTEN_SERP,
    noIndex: APP_ROUTES.SISTA_MINUTEN_SERP_NO_INDEX,
    prefix: '/sistaminuten',
  },
};

/** Sista Minuten first: the Companies path `/:q/:location` would also match `/sistaminuten/:q`. */
const TAB_MATCH_ORDER: SerpTabId[] = ['sistaminuten', 'places'];

/** Today's Deals and brand pages in both arms, though `/erbjudanden/:q` and the like match `/:q/:location`. */
const NON_TAB_PATHS = [
  APP_ROUTES.DEALS_SERP,
  APP_ROUTES.DEALS_SERP_NO_INDEX,
  APP_ROUTES.DEALS_SERP_PREVIEW,
  APP_ROUTES.DEALS_SERP_PREVIEW_NO_INDEX,
  APP_ROUTES.BRAND_DEALS_SERP,
  APP_ROUTES.BRAND_DEALS_SERP_NO_INDEX,
];

export const SERP_TAB_ROUTE_PATHS = TAB_MATCH_ORDER.flatMap((tab) => [
  TAB_PATHS[tab].noIndex,
  TAB_PATHS[tab].indexable,
]);

export const SERP_TAB_SWITCH_STATE = { serpTabSwitch: true } as const;

export function getSerpTabFromPath(pathname: string): SerpTabId | undefined {
  if (NON_TAB_PATHS.some((path) => matchPath(pathname, { path, exact: true }))) return undefined;
  return TAB_MATCH_ORDER.find((tab) =>
    [TAB_PATHS[tab].noIndex, TAB_PATHS[tab].indexable].some((path) => matchPath(pathname, { path, exact: true })),
  );
}

export function isIndexableSearch(q: string, location: string): boolean {
  return Boolean(getKeywordWithSlug(q)?.slug && getLocationWithSlug(location)?.slug);
}

export function getSerpTabPath(tab: SerpTabId, { q, location }: SerpSearch, isIndexable: boolean): string {
  const { noIndex, prefix } = TAB_PATHS[tab];
  if (!isIndexable) return noIndex;
  if (tab !== 'places' && !q && !location) return prefix;
  return `${prefix}/${slug(q || DEFAULT_SEARCH_KEYWORD)}/${locationSlug(location || DEFAULT_SEARCH_LOCATION)}`;
}

const isNearMe = (location: string) => locationSlug(location || '') === DEFAULT_MY_LOCATION_KEYWORD;

/** The query params that belong to the tab itself: everything but the search form's q, location and Nära mig coordinates. */
export function getOwnTabQuery(search: string, location: string): string {
  const params = new URLSearchParams(search);
  params.delete('q');
  params.delete('location');
  if (isNearMe(location)) {
    params.delete('lat');
    params.delete('lon');
  }
  return params.toString();
}

export const getSerpSearchIdentity = ({ q, location }: SerpSearch) => `${q}|${location}`;

/** A tab's URL for the current search: its path, the shared search, and its own params from memory. */
export function buildSerpTabHref(tab: SerpTabId, search: SerpSearch, memory?: SerpTabQuery): string {
  const isIndexable = isIndexableSearch(search.q, search.location);
  const params = new URLSearchParams(memory?.query);
  if (memory && memory.search !== getSerpSearchIdentity(search)) {
    params.delete('page');
  }
  if (!isIndexable) {
    if (search.q) params.set('q', search.q);
    if (search.location) params.set('location', search.location);
  }
  if (isNearMe(search.location) && search.lat && search.lon) {
    params.set('lat', `${search.lat}`);
    params.set('lon', `${search.lon}`);
  }
  const query = params.toString();
  return `${getSerpTabPath(tab, search, isIndexable)}${query ? `?${query}` : ''}`;
}
